import { Toaster } from "@npco/zeller-design-system";
import { ADT } from "pb.adt";
import type { Identity, Modify } from "pb.types";
import { useCallback, useEffect, useState } from "react";

import { logger } from "../../shared/logger";
import { retry } from "../../shared/retry";
import * as transport from "../../transport";
import { getDeviceSettings } from "./shared/get-device-settings";
import type { Layout } from "./shared/layout";
import { logout } from "./shared/logout";
import { Route } from "./shared/route";
import { useApiClient } from "./shared/use-api-client";
import { assignDeviceToEntity } from "./terminal-setup/assign-device-to-entity";
import { getDeviceId } from "./terminal-setup/get-device-id";
import { getEntities } from "./terminal-setup/get-entities";
import { PageDeviceName } from "./terminal-setup/page-device-name";
import { PageEntity } from "./terminal-setup/page-entity";
import { PageError } from "./terminal-setup/page-error";
import { PageInitialising } from "./terminal-setup/page-initialising";
import { PagePaired } from "./terminal-setup/page-paired";
import { PageTerminal } from "./terminal-setup/page-terminal";
import { pairDevice } from "./terminal-setup/pair-device";
import { removeDeviceFromEntity } from "./terminal-setup/remove-device-from-entity";
import { unpairDevice } from "./terminal-setup/unpair-device";

const resolve = transport.Resolve<"Terminal_setup">();

type Values = {
	entityId: string;
	deviceId: string;
	deviceName: string;
	terminalId: string;
	terminalName: string;
	terminalModel: string;
	terminalColor: string | undefined;
	terminalLogoUrl: string | undefined;
	lastTransactionTimestamp: string | undefined;
};

export const Page = ADT.define(
	{} as {
		Initialising: true;
		Error: {
			type?: "network" | "request" | "no-entities";
			onRetry?: () => void;
		};

		Paired: Identity<
			{
				onRename: (name: string) => Promise<void | Error>;
				onResetDevice: () => Promise<void>;
				onChangePairing: () => void;
			} & Values
		>;

		Entity: Identity<
			{
				onNext: (entityId: string) => void;
			} & Modify<
				Values,
				{
					optional: keyof Values;
					required: "deviceId";
				}
			>
		>;
		DeviceName: Identity<
			{
				onNext: (name: string) => Promise<void | Error>;
				onBack?: () => void;
			} & Modify<
				Values,
				{
					optional: keyof Values;
					required: "entityId" | "deviceId";
				}
			>
		>;
		Terminal: Identity<
			{
				changePairing?: { currentTerminalName: string };
				onNext: (terminalId: string) => Promise<void | Error>;
				onBack?: () => void;
			} & Modify<
				Values,
				{
					optional: keyof Values;
					required: "entityId" | "deviceId" | "deviceName";
				}
			>
		>;
	},
);

export type Page = ADT.define<typeof Page>;

export default Route({ query: "Terminal_setup" }, function (request) {
	const apiClient = useApiClient();
	const toaster = Toaster.useToaster();

	const [page, setPage] = useState<Page>(Page.Initialising());
	const [logoutPending, setLogoutPending] = useState(false);
	const onLogout = useCallback(async () => {
		logger.warn("setup: logging out");
		setLogoutPending(true);
		return logout({ apiClient, request });
	}, [apiClient, request]);

	useEffect(() => {
		//
		// DEVICE ID
		//
		const {
			deviceId: _deviceId,
			deviceModel: _deviceModel,
			deviceSerial: _deviceSerial,
		} = request.headers;

		if (!(_deviceModel && _deviceSerial)) {
			logger.error("terminal-setup: missing deviceModel and/or deviceSerial");
			setPage(Page.Error({ type: "request" }));
			return;
		}

		// Workaround for strange TypeScript out-of-closure narrowing behaviour.
		const deviceModel = _deviceModel;
		const deviceSerial = _deviceSerial;

		void (async () => {
			function onFetching() {
				// Reset to Initialising only if page not already Initialising.
				setPage((page) =>
					page.$type === "Initialising" ? page : Page.Initialising(),
				);
			}

			const deviceId = await new Promise<string>((resolve) => {
				if (_deviceId) {
					resolve(_deviceId);
					return;
				}

				void retry();
				async function retry() {
					onFetching();

					const deviceId = await getDeviceId({
						apiClient,
						deviceModel,
						deviceSerial,
					});

					if (deviceId instanceof Error) {
						logger.error("setup: failed to get device id", {
							errorName: deviceId.name,
							errorMessage: deviceId.message,
							errorStack: deviceId.stack,
						});
						if (deviceId.type === "OptionsInvalid") {
							setPage(Page.Error({ type: "request" }));
							return;
						}

						setPage(Page.Error({ type: "network", onRetry: retry }));
						return;
					}

					transport.Config({ deviceId });
					resolve(deviceId);
				}
			});

			//
			// DEVICE SETTINGS
			//
			await new Promise(() => {
				void retry();
				async function retry() {
					// Reset to Initialising only if page not already Initialising.
					onFetching();
					const settings = await getDeviceSettings({
						apiClient,
						deviceId,
					});
					if (settings instanceof Error) {
						return void (() => {
							logger.error("setup: failed to get device settings", {
								errorName: settings.name,
								errorMessage: settings.message,
								errorStack: settings.stack,
							});
							setPage(Page.Error({ type: "network", onRetry: retry }));
						})();
					}
					if (!settings) {
						setup({ deviceId });
						return;
					}
					if (!settings.terminal) {
						return void setPage(
							gotoTerminal({
								deviceId: settings.id,
								deviceName: settings.name,
								entityId: settings.entity.id,
								noBack: true,
							}),
						);
					}
					return void setPage(
						gotoPaired({
							deviceId: settings.id,
							deviceName: settings.name,
							entityId: settings.entity.id,
							terminalId: settings.terminal.id,
							terminalName: settings.terminal.name,
							terminalModel: settings.terminal.model,
							terminalColor: settings.terminal.color,
							terminalLogoUrl: settings.terminal.logoUrl,
							lastTransactionTimestamp: undefined,
						}),
					);
				}
			});
		})();

		type PageProps<TPageKey extends keyof ADT.Root<Page>> = Identity<
			Omit<ADT.Root<Page>[TPageKey], `on${string}`>
		>;

		function gotoPaired(props: PageProps<"Paired">) {
			return Page.Paired({
				...props,
				onRename: async (name) => {
					const response = await assignDeviceToEntity({
						apiClient,
						name,
						deviceUuid: props.deviceId,
						entityUuid: props.entityId,
					});
					if (response instanceof Error) {
						logger.error("setup: failed to assign device to entity", {
							errorName: response.name,
							errorMessage: response.message,
							errorStack: response.stack,
						});
						return response;
					}
					setPage(gotoPaired({ ...props, deviceName: name }));
					return;
				},
				onResetDevice: async () => {
					logger.warn("setup: resetting device");
					await removeDeviceFromEntity({
						apiClient,
						deviceUuid: props.deviceId,
						entityUuid: props.entityId,
					});

					setup(props);
				},
				onChangePairing: () => {
					logger.warn("setup: unpairing device");
					setPage({
						...gotoTerminal({
							...props,
							changePairing: { currentTerminalName: props.terminalName },
						}),
						onBack: () => setPage(gotoPaired(props)),
					});
				},
			});
		}

		function setup({ deviceId }: Pick<Values, "deviceId">) {
			void retry();
			async function retry() {
				// Reset to Initialising only if page not already Initialising.
				setPage((page) =>
					page.$type === "Initialising" ? page : Page.Initialising(),
				);

				const response = await getEntities({ apiClient });
				if (response instanceof Error) {
					logger.error("setup: failed to get entities", {
						errorName: response.name,
						errorMessage: response.message,
						errorStack: response.stack,
					});
					setPage(Page.Error({ type: "network", onRetry: retry }));
					return;
				}

				const defaultEntityId = response.defaultEntityUuid;
				const relations = response.entityRelations;

				if (!relations || relations.length === 0) {
					logger.error("setup: no entities for customer");
					setPage(Page.Error({ type: "no-entities" }));
					return;
				}

				const isMultiEntity = relations.length > 1;

				if (isMultiEntity) {
					setPage(gotoEntity({ deviceId }));
				} else {
					setPage(
						gotoDeviceName({
							deviceId,
							entityId: defaultEntityId,
							noBack: false,
						}),
					);
				}
			}
		}

		function gotoEntity(props: PageProps<"Entity">) {
			return Page.Entity({
				...props,
				onNext: (entityId) => {
					setPage(gotoDeviceName({ ...props, entityId }));
				},
			});
		}

		function gotoDeviceName(
			props: PageProps<"DeviceName"> & { noBack?: boolean },
		) {
			return Page.DeviceName({
				...props,
				onBack: props.noBack ? undefined : () => setPage(gotoEntity(props)),
				onNext: async (name) => {
					const response = await assignDeviceToEntity({
						apiClient,
						deviceUuid: props.deviceId,
						entityUuid: props.entityId,
						name,
					});

					if (response instanceof Error) {
						logger.error("setup: assign device to entity error", {
							errorName: response.name,
							errorMessage: response.message,
							errorStack: response.stack,
						});

						toaster.toast({
							status: "negative",
							message: "Failed to assign device.",
						});

						return response;
					}

					return void setPage(
						gotoTerminal({
							...props,
							deviceName: name,
							noBack: false,
						}),
					);
				},
			});
		}

		function gotoTerminal(props: PageProps<"Terminal"> & { noBack?: boolean }) {
			return Page.Terminal({
				...props,
				onBack: props.noBack ? undefined : () => setPage(gotoDeviceName(props)),
				onNext: async (terminalId: string) => {
					const onError = (response: Error) => {
						logger.error("setup: terminal on next", {
							errorName: response.name,
							errorMessage: response.message,
							errorStack: response.stack,
						});

						toaster.toast({
							status: "negative",
							message: "Failed to pair Zeller Terminal.",
						});
					};

					// Skip unpair/pair if same target terminal.
					if (terminalId !== props.terminalId) {
						if (props.terminalId) {
							const response = await unpairDevice({
								apiClient,
								deviceId: props.deviceId,
								terminalId: props.terminalId,
							});
							if (response instanceof Error) {
								return void onError(response);
							}
						}
						{
							const response = await pairDevice({
								apiClient,
								deviceId: props.deviceId,
								terminalId,
							});

							if (response instanceof Error) {
								return void onError(response);
							}
						}
					}

					const settings = await retry(
						{ limit: 5, backoffMs: 1500 },
						async () => {
							const settings = await getDeviceSettings({
								apiClient,
								deviceId: props.deviceId,
							});

							if (settings instanceof Error) {
								return settings;
							}
							if (!settings?.terminal) {
								return new Error("Failed to pair Zeller Terminal.");
							}
							const { terminal } = settings;

							// Ensure that we're getting the latest terminal pairing data.
							if (terminal.id !== terminalId) {
								return new Error("Failed to pair Zeller Terminal.");
							}
							return {
								...settings,
								terminal,
							};
						},
					);
					if (settings instanceof Error) {
						return void onError(settings);
					}

					setPage(
						gotoPaired({
							...props,
							deviceId: settings.id,
							deviceName: settings.name,
							entityId: settings.entity.id,
							terminalId: settings.terminal.id,
							terminalName: settings.terminal.name,
							terminalModel: settings.terminal.model,
							terminalColor: settings.terminal.color,
							terminalLogoUrl: settings.terminal.logoUrl,
							lastTransactionTimestamp: undefined,
						}),
					);
				},
			});
		}
	}, [apiClient, request, toaster]);

	return render({
		page,
		logout: { onClick: onLogout, pending: logoutPending },
	});
});

export namespace render {
	export type Props = {
		page: Page;
		logout: {
			onClick: () => Promise<void>;
			pending: boolean;
		};
	};
}

export function render({ page, logout }: render.Props) {
	const defaultLayoutProps = {
		onLogout: logout.onClick,
		pending: logout.pending ? "logout" : false,
	} satisfies Layout.Props;

	if (page.$type === "Initialising") {
		return (
			<PageInitialising
				{...defaultLayoutProps}
				onClose={() =>
					void resolve.Error("Operation Interrupted", {
						message: "Setup was closed while still loading.",
					})
				}
			/>
		);
	}

	if (page.$type === "Paired") {
		return (
			<PagePaired
				page={page}
				{...defaultLayoutProps}
				onClose={() => void resolve.Ok(true)}
			/>
		);
	}

	if (page.$type === "Error") {
		return <PageError page={page} {...defaultLayoutProps} />;
	}

	const onClose = () =>
		void resolve.Error("Setup Required", {
			message: "Setup was closed before it was completed.",
		});

	if (page.$type === "Entity") {
		return <PageEntity page={page} {...defaultLayoutProps} onClose={onClose} />;
	}

	if (page.$type === "DeviceName") {
		return (
			<PageDeviceName page={page} {...defaultLayoutProps} onClose={onClose} />
		);
	}

	if (page.$type === "Terminal") {
		return (
			<PageTerminal page={page} {...defaultLayoutProps} onClose={onClose} />
		);
	}

	// Unhandled.
	/* v8 ignore next -- defensive fallback, all page variants handled above */
	return <PageError {...defaultLayoutProps} />;
}
